

/*
 * Date: 2025
 * Description: Login page..
 * Author: Philippe Leroux @ Skitsc
 */

//Modules
import { ReactElement , useContext, useState , ChangeEvent , useEffect } from "react"
import { Box, Typography , TextField , Button, Paper , IconButton , InputAdornment, Collapse } from "@mui/material"
import { Link } from "react-router-dom"

//Components
import Footer from "../components/utils/footer"

//Interfaces
import { i_user_login } from "../interfaces/user.interface"
import { i_alert, i_alert_control, i_initial_props } from "../interfaces/utility.interface"

//Constants
import { defaultLoginUser , default_alert } from "../utils/constant"

//Middlewares
import { sanitizeLogin } from "../middlewares/main.middleware"
import { validateEmpty , validatePassword } from "../middlewares/validator"

//API calls & utils
import { ENV, handleFetch } from "../api/fetch"

//Context
import { MainContext } from "../context/context"
import { SocketContext } from "../context/socket.context"

//Components
import AlertTop from "../components/utils/alert"

//Icons
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const Login = (props : i_initial_props ) : ReactElement => {
    const { HandleLogin } = useContext(MainContext)
    const socket = useContext(SocketContext)
    const [ display , setDisplay ] = useState<'login' | 'register' | 'otp'>('login')
    const [ form , setForm ] = useState<i_user_login>(defaultLoginUser)
    const [ visibility, setVisibility ] = useState<boolean>(false)
    const [ errors , setErrors ] = useState<i_user_login>(defaultLoginUser)
    const [ alert , setAlert ] = useState<i_alert>(default_alert)
    const [ loading , setLoading ] = useState<boolean>(false)
    
    const handleField = ( e : ChangeEvent<HTMLInputElement> ) => {
        const { name, value } = e.target
        setForm({...form, [name] : value})
        setErrors({...errors, [name] : ''})
    }
    const handleSubmit = async() => {
        setLoading(true)
        const [ valid , field , msg ] = sanitizeLogin(form)
        if(valid){
            const res = await handleFetch('/'+display, 'POST', true, form)
            if(res.type === 'Success'){
                if(ENV === 'development'){
                    if(display === 'login'){
                        socket.emit('join', { roomId : res.data.type , user_id : res.data._id , socket_id : socket.id })
                        HandleLogin(res.data)
                    }
                }
                if(ENV === 'production'){
                    const ok = res.data._id ? true : false
                    if(display === 'otp' || ok){
                        socket.emit('join', { roomId : res.data.type , user_id : res.data._id , socket_id : socket.id })
                        HandleLogin(res.data)
                    }
                    if(display === 'login'){
                        setDisplay('otp')
                        setForm({...form, otp : '' , phone : res.data.phone , session_uuid : res.data.session_uuid  })
                    }
                }
                if(display === 'register'){
                    setAlert({open : true, message : res.message, severity:'success'})
                    setForm(defaultLoginUser)
                    setTimeout(() => {
                        setAlert(default_alert)
                    }, 3000)
                }
            }else{
                setAlert({open : true, message : res.message , severity: 'error'})
            }
        }else{
            setErrors({...errors, [field] : msg})
        }
        setLoading(false)
    }
    const handleResend = async() => {
        setLoading(true)
        const res = await handleFetch('/login', 'POST', true, form)
        if(res.type === 'Success'){
            setAlert({open : true, message : "Code sent, please check your phone", severity:'success'})
            setForm({...form, otp : '' , phone : res.data.phone , session_uuid : res.data.session_uuid  })
            setTimeout(() => {
                setAlert(default_alert)
            }, 3000)
        } else{
            setAlert({open : true, message : res.message, severity: 'error'})
        }
        setLoading(false)
    }
    useEffect(() => {
        if(errors.username !== '' && validateEmpty(form.username)) setErrors({...errors, username : '' })
        if(errors.password !== '' && validatePassword(form.password)) setErrors({...errors, password : '' })
    },[form , errors])
    useEffect(() => {  
        const handleKeyPress = (event : any) => {
            if (event.key === 'Enter')  handleSubmit();
        };
        document.addEventListener('keydown', handleKeyPress);
        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    //eslint-disable-next-line react-hooks/exhaustive-deps 
    }, [form]);
    const alert_props : i_alert_control = {
        alert : alert,
        onClose : () => setAlert(default_alert),
        float : 'top',
        top : 300
    }
    const handleTransition = () : void => {
        setDisplay(display === 'login'? 'register' : 'login')
        setForm(defaultLoginUser)
    }
    return (
        <Box>
            <AlertTop {...alert_props}/>
            <Box sx={{ height : '96vh' , backgroundImage : "url('/img/bg3.png')" , backgroundSize : 'cover' , backgroundPosition : 'center'}}>
                <Box sx={{ paddingTop : '30vh'}}>

                </Box>
                <Paper elevation={6} sx={{ display : 'flex' , flexDirection : 'column' , justifyContent : 'center' , border : '1px solid black' , maxWidth : '600px' , marginLeft : 'auto', marginRight : 'auto' , borderRadius : '8px' , boxShadow : '5px 10px #B71F26 '}}>
                    <Typography sx={{ textAlign : 'center' , margin : '2vh' , fontSize : 22 , fontWeight : 'bolder'}}>{display === 'login' ? 'Welcome Back !' : display === 'register' ? 'Contact-us' : "Verify your phone number" }</Typography>
                        <Collapse in={display === 'login' ? true : false}>
                            <Box sx={{ display : 'flex' , alignItems : 'center' , margin: 1}}>
                                <Typography sx={{ width : '35%' , textAlign : 'center' , fontWeight : 'bold' , fontSize : 18}}>Username : </Typography>
                                <TextField value={form.username} onChange={handleField} name={'username'} error={errors.username !== '' ? true : false} helperText={errors.username} size={'small'} sx={{ width : '65%' , maxWidth : '250px'}} />
                            </Box>
                            <Box sx={{ display : 'flex' , alignItems : 'center' , margin: 1}}>
                                <Typography sx={{ width : '35%' , textAlign : 'center' , fontWeight : 'bold' , fontSize : 18}}>Password : </Typography>
                                <TextField value={form.password} onChange={handleField} type={visibility ? 'text' : 'password'} name={'password'} error={errors.password !== '' ? true : false} helperText={errors.password} size={'small'} sx={{ width : '65%' , maxWidth : '250px'}}
                                slotProps={{
                                    input: {
                                        endAdornment: 
                                        <InputAdornment position="end">
                                            <IconButton aria-label={ visibility ? 'hide the password' : 'display the password' } onClick={() => setVisibility( visibility ? false : true  )} edge="end" >
                                                {visibility ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                }}}/>
                            </Box>
                            <Box sx={{ display : 'flex', justifyContent : 'center', marginTop : '0.5vh'}}>
                                <Box component={Link} to="/forgot_password" sx={{ fontSize : 14 , color : 'black' ,}}>Forgot Password?</Box>
                            </Box>
                            <Box sx={{ display : 'flex' , justifyContent : 'center' , marginTop : '0.5vh'}}>
                                <Box sx={{ fontSize : 14 , color : 'black'}}>New here? <Box onClick={ () => handleTransition()} sx={{ display : 'inline' , textDecoration : 'underline' , cursor : 'pointer'}}>Request an access</Box></Box>
                            </Box>
                        </Collapse>
                        <Collapse in={display === 'register' ? true : false} timeout={'auto'}>
                            <Box sx={{ display : 'flex', justifyContent : 'center' , marginBottom : '2vh'}}>
                                <Typography sx={{ textAlign : 'center' , maxWidth : '350px' , fontSize : 20}}>Leave us your email in order to contact you, for the following steps</Typography>
                            </Box>
                            <Box sx={{ display : 'flex' , alignItems : 'center' , margin: 1}}>
                                <Typography sx={{ width : '35%' , textAlign : 'center' , fontWeight : 'bold' , fontSize : 18}}>Email* : </Typography>
                                <TextField value={form.email} onChange={handleField} name={'email'} error={errors.email !== '' ? true : false} helperText={errors.email} size={'small'} sx={{ width : '65%' , maxWidth : '300px'}} />
                            </Box>
                            <Box sx={{ display : 'flex' , alignItems : 'center' , margin: 1}}>
                                <Typography sx={{ width : '35%' , textAlign : 'center' , fontWeight : 'bold' , fontSize : 18}}>Note : </Typography>
                                <TextField multiline type='text' rows={4} value={form.note} onChange={handleField} name={'note'} error={errors.note !== '' ? true : false} helperText={errors.note} size={'small'} sx={{ width : '65%' , maxWidth : '300px'}} />
                            </Box>
                            <Box sx={{ display : 'flex' , justifyContent : 'center' , marginTop : '0.5vh'}}>
                                <Box onClick={ () => handleTransition()} sx={{ display : 'inline' , textDecoration : 'underline' , cursor : 'pointer'}}>Go back to login</Box>
                            </Box>
                        </Collapse>
                        <Collapse in={display === 'otp' ? true : false}>
                            <Box sx={{ display : 'flex' , alignItems : 'center' , margin: 1 , flexDirection : 'column'}}>
                                <Box sx={{ width : '100%'}}>
                                    <Typography sx={{ textAlign : 'center' , marginBottom : '1vh'}}>{"Enter the 6-digit verification code sent to ******" + form.phone.slice(-4) }</Typography>
                                </Box>
                                <TextField value={form.otp} onChange={handleField} name={'otp'} error={errors.otp !== '' ? true : false} helperText={errors.otp} size={'small'} sx={{ width : '65%' , maxWidth : '250px'}} />
                                <Box sx={{ display : 'flex', justifyContent : 'center', marginTop : '1vh'}}>
                                    <Box onClick={() => handleResend()} sx={{ display : 'inline' , textDecoration : 'underline' , cursor : 'pointer'}}>Resend Code</Box>
                                </Box>
                            </Box>
                        </Collapse>
                
                    <Box sx={{ display : 'flex', justifyContent : 'center', margin : '2vh'}}>
                        <Button type={'button'} loading={loading} onClick={() => handleSubmit()} disabled={loading} variant="contained" color="primary" sx={{ width : '40%' , fontSize : 18}}>Submit</Button>
                    </Box>
                </Paper>
            </Box>
            <Footer/>
        </Box>
    )
}

export default Login